import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { form, FormField, required } from '@angular/forms/signals';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  PropertyCondition,
  PropertyConditionsDto,
  PropertyConstructionDto,
  PropertyConstructionMaterial,
  PropertyConstructionPeriod,
  PropertyDefinitionDto,
  PropertyDpeDto,
  PropertyExposure,
  PropertyInsulationDto,
  PropertyOrientation,
  PropertyRoofMaterial,
  PropertyStanding,
  PropertyType,
  PropertyView,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AuraNumberInputComponent } from '@workspace/ng-aura';
import { EtxeEnumDisplayPipe } from '../../../pipes/etxe-enum-display.pipe';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';

export interface PropertyInsulationModel {
  walls: string | null;
  roof: string | null;
  floor: string | null;
  glazing: string | null;
  envelope: string | null;
}

export interface PropertyConditionsModel {
  interior: PropertyCondition | null;
  commonAreas: PropertyCondition | null;
}

export interface PropertyConstructionMaterialModel {
  walls: PropertyConstructionMaterial[];
  roof: PropertyRoofMaterial[];
}

export interface PropertyConstructionModel {
  year: PropertyConstructionPeriod | null;
  material: PropertyConstructionMaterialModel;
  buildingHeight: number | null;
  groundFootprint: number | null;
  ceilingHeight: number | null;
  insulation: PropertyInsulationModel;
  standing: PropertyStanding | null;
  openings: number | null;
  orientation: PropertyOrientation[];
  exposure: PropertyExposure | null;
  view: PropertyView[];
  precision: string;
  conditions: PropertyConditionsModel;
}

@Component({
  selector: 'etxe-property-construction-form',
  standalone: true,
  imports: [
    CommonModule,
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    AuraNumberInputComponent,
    EtxeEnumDisplayPipe,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatIconModule,
  ],
  templateUrl: './construction-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyConstructionFormComponent extends AbstractSignalFormComponent<
  PropertyConstructionDto,
  PropertyConstructionModel
> {
  hideStepHeader = input<boolean>(false);
  propertyDefinition = input<PropertyDefinitionDto | null>(null);
  propertyDpe = input<PropertyDpeDto | null>(null);

  isHouse = computed(() => this.propertyDefinition()?.propertyType === PropertyType.HOUSE);

  propertyConditionTypes = Object.values(PropertyCondition);
  constructionMaterialTypes = Object.values(PropertyConstructionMaterial);
  roofMaterialTypes = Object.values(PropertyRoofMaterial);
  orientationTypes = Object.values(PropertyOrientation);
  exposureTypes = Object.values(PropertyExposure);
  constructionPeriodTypes = Object.values(PropertyConstructionPeriod);
  propertyStandingTypes = Object.values(PropertyStanding);
  propertyViewTypes = Object.values(PropertyView);

  override model: WritableSignal<PropertyConstructionModel> = signal<PropertyConstructionModel>({
    year: null,
    material: {
      walls: [],
      roof: [],
    },
    buildingHeight: null,
    groundFootprint: null,
    ceilingHeight: null,
    insulation: {
      walls: null,
      roof: null,
      floor: null,
      glazing: null,
      envelope: null,
    },
    standing: null,
    openings: null,
    orientation: [],
    exposure: null,
    view: [],
    precision: '',
    conditions: {
      interior: null,
      commonAreas: null,
    },
  });

  override formTree = form(this.model, (b) => {
    required(b.standing);
    required(b.conditions.interior);
    required(b.conditions.commonAreas, { when: () => !this.isHouse() });
  });

  protected override buildModelFromDto(dto: PropertyConstructionDto): PropertyConstructionModel {
    const tech = this.propertyDpe()?.technical;

    const ceilingHeight =
      dto?.ceilingHeight ?? (tech?.ceilingHeight && tech.ceilingHeight > 0 ? tech.ceilingHeight : null);

    const insulation: PropertyInsulationModel = {
      walls: dto?.insulation?.walls ?? tech?.wallInsulation ?? null,
      roof: dto?.insulation?.roof ?? tech?.roofInsulation ?? null,
      floor: dto?.insulation?.floor ?? tech?.floorInsulation ?? null,
      glazing: dto?.insulation?.glazing ?? tech?.glazingQuality ?? null,
      envelope: dto?.insulation?.envelope ?? tech?.envelopeQuality ?? null,
    };

    return {
      year: dto?.year ?? null,
      material: {
        walls: dto?.material?.walls ?? [],
        roof: dto?.material?.roof ?? [],
      },
      buildingHeight: dto?.buildingHeight ?? null,
      groundFootprint: dto?.groundFootprint ?? null,
      ceilingHeight,
      insulation,
      standing: dto?.standing ?? null,
      openings: dto?.openings ?? null,
      orientation: dto?.orientation ?? [],
      exposure: dto?.exposure ?? null,
      view: Array.isArray(dto?.view) ? dto.view : dto?.view ? [dto.view] : [],
      precision: dto?.precision ?? '',
      conditions: {
        interior: dto?.conditions?.interior ?? null,
        commonAreas: this.isHouse() ? null : (dto?.conditions?.commonAreas ?? null),
      },
    };
  }

  protected override buildDtoFromModel(model: PropertyConstructionModel): PropertyConstructionDto {
    const rawDto: PropertyConstructionDto = {
      ...(this.dto() || ({} as PropertyConstructionDto)),
      year: model.year ?? undefined,
      material:
        model.material.walls.length > 0 || model.material.roof.length > 0
          ? {
              walls: model.material.walls.length > 0 ? model.material.walls : undefined,
              roof: model.material.roof.length > 0 ? model.material.roof : undefined,
            }
          : undefined,
      buildingHeight: model.buildingHeight ?? undefined,
      groundFootprint: model.groundFootprint ?? undefined,
      ceilingHeight: model.ceilingHeight ?? undefined,
      insulation:
        model.insulation.walls ||
        model.insulation.roof ||
        model.insulation.floor ||
        model.insulation.glazing ||
        model.insulation.envelope
          ? ({
              walls: model.insulation.walls || undefined,
              roof: model.insulation.roof || undefined,
              floor: model.insulation.floor || undefined,
              glazing: model.insulation.glazing || undefined,
              envelope: model.insulation.envelope || undefined,
            } as PropertyInsulationDto)
          : undefined,
      standing: model.standing ?? undefined,
      openings: model.openings ?? undefined,
      orientation: model.orientation.length > 0 ? model.orientation : undefined,
      exposure: model.exposure ?? undefined,
      view: model.view.length > 0 ? model.view : undefined,
      precision: model.precision || undefined,
      conditions: {
        interior: model.conditions.interior ?? undefined,
        commonAreas: this.isHouse() ? undefined : (model.conditions.commonAreas ?? undefined),
      } as PropertyConditionsDto,
    };

    return PropertyDataCleaner.deepClean(rawDto) as PropertyConstructionDto;
  }
}
